import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useApi } from '../../contexts/ApiProvider'
import { useSurvey } from '../../contexts/SurveyProvider'
import { SilhouettePlotChart } from './SilhouettePlotChart'
import { HelpDrawerSilhouettePlot } from './HelpDrawer'
import { AppToaster } from '../toaster'
import { errorObjectToString } from '../../ApiClient'
import { NonIdealState, Spinner, Card, Button } from '@blueprintjs/core'
import './SegmentationExplorerChart.css'

/**
 * SegmentationExplorerChart is a wrapper component that handles lazy loading
 * of silhouette plot data only when the tab is opened.
 *
 * This prevents loading heavy data (silhouette_samples + clusters_assigned)
 * until it's actually needed, improving initial load performance.
 *
 * @param {Object} props - Component props
 * @param {Object} props.segmentation - The segmentation object (without heavy data)
 * @param {boolean} props.isActive - Whether the silhouette tab is currently active
 * @returns {JSX.Element} The rendered component
 */
export function SegmentationExplorerChart({ segmentation, isActive }) {
  const [silhouetteData, setSilhouetteData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const containerRef = useRef(null)
  const api = useApi()
  const { surveyId } = useSurvey()

  const handleDrawerOpen = () => setIsDrawerOpen(true)
  const handleDrawerClose = () => setIsDrawerOpen(false)

  /**
   * Fetch silhouette data from the API
   */
  const fetchSilhouetteData = useCallback(async () => {
    if (!segmentation || !segmentation.id || !surveyId) return
    if (segmentation.is_imported) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await api.get(
        `/survey/${surveyId}/segmentation/${segmentation.id}/silhouette`
      )

      if (response.ok) {
        setSilhouetteData(response.body)
      } else {
        const message =
          errorObjectToString(response.body?.messages?.json) ||
          'Failed to load silhouette data'
        setError(message)
        AppToaster.show({
          message,
          intent: 'danger',
          icon: 'error',
        })
      }
    } catch (err) {
      const message = 'An error occurred while loading silhouette data'
      setError(message)
      AppToaster.show({ message, intent: 'danger', icon: 'error' })
    } finally {
      setIsLoading(false)
    }
  }, [api, surveyId, segmentation])

  /**
   * Fetch data when tab becomes active and data hasn't been loaded yet
   */
  useEffect(() => {
    if (isActive && !silhouetteData && !isLoading && !error) {
      fetchSilhouetteData()
    }
  }, [isActive, silhouetteData, isLoading, error, fetchSilhouetteData])

  /**
   * Reset data when segmentation changes
   */
  useEffect(() => {
    setSilhouetteData(null)
    setError(null)
  }, [segmentation?.id])

  // Don't render anything if tab is not active
  if (!isActive) {
    return null
  }

  if (segmentation?.is_imported) {
    return (
      <NonIdealState
        icon="info-sign"
        title="Silhouette plot not available"
        description="This segmentation was imported from uploaded data. Silhouette scores require the original input variables used to compute the clusters, which are not available for imported segmentations."
      />
    )
  }

  // Show error state
  if (error) {
    return (
      <NonIdealState
        icon="error"
        title="Failed to load silhouette data"
        description={error}
        action={{
          text: 'Retry',
          onClick: fetchSilhouetteData,
          intent: 'primary',
        }}
      />
    )
  }

  // Show loading state
  if (isLoading || !silhouetteData) {
    return (
      <NonIdealState
        icon={<Spinner />}
        title="Loading silhouette data..."
        description="Please wait while we fetch the silhouette plot data"
      />
    )
  }

  // Render the chart with the loaded data
  return (
    <div
      ref={containerRef}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      <div className="chart-title">
        <h2>
          Silhouette Plot - {segmentation ? segmentation.variable.name : ''}
          <Button icon="help" minimal={true} onClick={handleDrawerOpen} />
        </h2>
      </div>
      <Card className="segmentation-explorer-chart-card" style={{ flex: 1, minHeight: 0 }}>
        <SilhouettePlotChart
          silhouetteData={silhouetteData}
          isLoading={false}
          containerRef={containerRef}
        />
      </Card>
      <HelpDrawerSilhouettePlot isOpen={isDrawerOpen} onClose={handleDrawerClose} />
    </div>
  )
}
